import axios from 'axios';
import { inspect } from 'util';

const { NEXT_PUBLIC_PUT_SESSION_URL, ACC_URL_DATA } = process.env;

declare global {
  // eslint-disable-next-line no-var
  var loggerInstance: CallableFunction;
}

type PrearrivalSessionPrimitive = string | number | boolean | null;
type PrearrivalSessionValue = PrearrivalSessionPrimitive | PrearrivalSessionObject | PrearrivalSessionValue[];
type PrearrivalSessionObject = {
  [key: string]: PrearrivalSessionValue;
};

export type PrearrivalSessionData = {
  brandCode?: string;
  marshaCode?: string;
  confirmationNumber?: string;
  isMemberFlg?: string;
  nonBrandedFlg?: string;
  reservation?: PrearrivalSessionValue;
  passions?: PrearrivalSessionValue;
  accDown?: PrearrivalSessionValue;
  errorData?: PrearrivalSessionValue;
};

type PrearrivalSessionUpdateResult = unknown | null;

const toPrearrivalSessionValue = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet()
): PrearrivalSessionValue | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack ?? null,
    };
  }

  if (Array.isArray(value)) {
    return value.map(item => toPrearrivalSessionValue(item, seen) ?? null);
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return undefined;
    }

    seen.add(value);
    const serializedValue: PrearrivalSessionObject = {};

    Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
      const sanitizedValue = toPrearrivalSessionValue(nestedValue, seen);

      if (sanitizedValue !== undefined) {
        serializedValue[key] = sanitizedValue;
      }
    });

    seen.delete(value);
    return serializedValue;
  }

  return undefined;
};

const buildPrearrivalSessionUpdatePayload = (prearrivalData: PrearrivalSessionData, sessionToken?: string) => {
  const prearrivalSessionData: Record<string, PrearrivalSessionValue> = {};

  if (prearrivalData?.brandCode) {
    prearrivalSessionData.brandCode = String(prearrivalData.brandCode);
  }

  if (prearrivalData?.marshaCode) {
    prearrivalSessionData.marshaCode = String(prearrivalData.marshaCode);
  }

  if (prearrivalData?.confirmationNumber) {
    prearrivalSessionData.confirmationNumber = String(prearrivalData.confirmationNumber);
  }

  if (prearrivalData?.isMemberFlg) {
    prearrivalSessionData.isMemberFlg = String(prearrivalData.isMemberFlg);
  }

  if (prearrivalData?.nonBrandedFlg) {
    prearrivalSessionData.nonBrandedFlg = String(prearrivalData.nonBrandedFlg);
  }

  const reservation = toPrearrivalSessionValue(prearrivalData?.reservation);
  if (reservation !== undefined) {
    prearrivalSessionData.reservation = reservation;
  }

  const passions = toPrearrivalSessionValue(prearrivalData?.passions);
  if (passions !== undefined) {
    prearrivalSessionData.passions = passions;
  }

  const accDown = toPrearrivalSessionValue(prearrivalData?.accDown);
  if (accDown !== undefined) {
    prearrivalSessionData.accDown = accDown;
  }

  const errorData = toPrearrivalSessionValue(prearrivalData?.errorData);
  if (errorData !== undefined) {
    prearrivalSessionData.errorData = errorData;
  }

  if (!Object.keys(prearrivalSessionData).length) {
    return null;
  }

  return {
    createOrUpdate: {
      Prearrival: prearrivalSessionData,
    },
    sessionToken: sessionToken ?? '',
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchReservationData = async (marshaCode: any, confirmationNumber: any): Promise<any> => {
  const { log } = global.loggerInstance('ReservationService:fetchReservationData');

  let response = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorData: any = {
    url: ACC_URL_DATA,
    show: true,
    status: '400',
    accDown: {},
  };

  // TODO: use codes from request
  if (marshaCode === null || confirmationNumber === null) {
    response = { errorData };
  } else {
    // TODO turn this into env variable

    const uri = ACC_URL_DATA + '?marshaCode=' + marshaCode + '&confirmationNumber=' + confirmationNumber;
    log.debug('[ReservationService] API from accDemo and others', uri);
    try {
      await axios.get(uri).then(({ data }) => {
        log.debug('[ReservationService] Data from fetchReservation', data);
        response = data;
      });
    } catch (error) {
      const accDown = error instanceof Error ? { message: error.message, name: error.name } : {};
      response = { ...errorData, accDown };
      log.debug('[ReservationService] Error Data Message', error);
    }

    return {
      response,
    };
  }
};

export const updateBrandCode = async (
  brandCode: string,
  sessionToken?: string,
  requestId = ''
): Promise<PrearrivalSessionUpdateResult> => {
  return updatePrearrivalSession({ brandCode }, sessionToken, requestId);
};

export const updatePrearrivalSession = async (
  prearrivalData: PrearrivalSessionData,
  sessionToken?: string,
  requestId = ''
): Promise<PrearrivalSessionUpdateResult> => {
  const updateUrl = `${NEXT_PUBLIC_PUT_SESSION_URL}`;
  const payload = buildPrearrivalSessionUpdatePayload(prearrivalData, sessionToken);
  const headers = {
    headers: {
      Cookie: `sessionID=${sessionToken}`,
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      'x-request-id': requestId,
    },
  };

  let result;

  if (!payload) {
    return null;
  }

  if (!updateUrl.includes('undefined')) {
    try {
      const apiStartTime = new Date().getTime();
      // console.log(`[Prearrival Session] update session API call started:, ${headers}`);
      // console.log(`[Prearrival Session] updateUrl:, ${updateUrl}`);
      // console.log(`[Prearrival Session payload', ${inspect(payload)}`);
      result = await axios.patch(updateUrl, payload, headers).then(res => res.data);
      console.log(
        '[Prearrival Session] update session API Success:',
        apiStartTime,
        new Date().getTime(),
        inspect(result)
      );
    } catch (error) {
      console.log(`[Prearrival Session] update session API Failed:, ${error}`);
    }
  }
  return result;
};
